import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ApiService from '../Components/apiService'; // Import ApiService class
import { BASE_URL, DateFormat } from '../Components/Constant';
import {
    Layout,
    Card,
    Typography,
    Table,
    Tag,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Popconfirm,
    Space,
    Avatar,
    Row,
    Col,
    Checkbox,
    Tooltip,
    message, // Remove static message import if not needed elsewhere
} from 'antd';
import {
    ArrowLeftOutlined,
    PlusOutlined,
    DeleteOutlined,
    EyeOutlined,
    CloudOutlined,
    TableOutlined,
    SettingOutlined,
    ContainerOutlined,
} from '@ant-design/icons';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;


const AdminUserDetails = () => {

    const navigate = useNavigate();
    const { orgId } = useParams();


    const [users, setUsers] = useState([]);

    const [messageApi, contextHolder] = message.useMessage();
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editUser, setEditUser] = useState(null);

    const excludedFields = [
        'password_digest',
        'reporting_path',
        'reset_password_sent_at',
        'reset_password_token',
        'updated_at',
        'chat_ids',
        'password',
    ];


    const fetchUsers = async () => {
        try {
            const apiService = new ApiService(
                `${BASE_URL}/super_admin/users/${orgId}`,
                { 'Content-Type': 'application/json' },
                'GET'
            );
            const data = await apiService.makeCall();
            setUsers(data.users || []);
            console.log('Fetched users:', data.users);
        }
        catch (error) {
            console.error('Error fetching users:', error);
            const errorMessage = error.message || 'Failed to fetch users';
            messageApi.error(errorMessage);
        }
    }

    useEffect(() => {
        fetchUsers();
    }, [orgId]);


    const handleViewUser = (user) => {
        setSelectedUser(user);
        setViewModalVisible(true);
    };

    const handleCloseModal = () => {
        setViewModalVisible(false);
        setSelectedUser(null);
    };

    const handleEditModal = () => {
        setEditUser(selectedUser);
        setIsEditMode(true);


    };

    const handleEditChange = (key, value) => {
        setEditUser(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveEdit = async () => {
        console.log('Saving edited user:', editUser);
        try {

            const body = {
                "object_name": "User",
                "data": {
                    "_id": editUser._id,
                    "Name": editUser.Name,
                    "username": editUser.username,
                    "is_active": editUser.is_active,
                    "email": editUser.email
                },
                "org_id": orgId,
                "user_id": editUser._id
            }
            const apiService = new ApiService(
                `${BASE_URL}/super_admin/insert_or_update_records`,
                { 'Content-Type': 'application/json' },
                'POST',
                body
            );
            await apiService.makeCall();
            messageApi.success('User updated successfully');
            setIsEditMode(false);
            setViewModalVisible(false);
            fetchUsers();
        } catch (error) {
            messageApi.error('Failed to update user');
        }
    };


    const handleDeleteUser = async (userId) => {
        try {
            const apiService = new ApiService(
                `${BASE_URL}/super_admin/users/${userId}`,
                { 'Content-Type': 'application/json' },
                'DELETE'
            );
            await apiService.makeCall();
            messageApi.success('User deleted successfully');
            fetchUsers(); // Refresh the user list after deletion
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMessage = error.message || 'Failed to delete user';
            messageApi.error(errorMessage);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'Name',
            key: 'Name',
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (value) => (
                <Checkbox checked={!!value} disabled />
            ),
        },
        {
            title: 'User Role',
            dataIndex: 'user_role',
            key: 'user_role',
            render: (_, record) => record.user_role || record.role || '',
        },
        {
            title: 'Super Admin',
            dataIndex: 'SuperAdmin',
            key: 'SuperAdmin',
            render: (value) => (
                <Checkbox checked={!!value} disabled />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View User">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewUser(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this user?"
                        onConfirm={() => handleDeleteUser(record.key)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete User">
                            <Button danger icon={<DeleteOutlined />} type="link" />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            {contextHolder} {/* Add this line */}
            <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                style={{ marginRight: 16 }}
                            >
                                Back
                            </Button>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                                <CloudOutlined style={{ marginRight: 8 }} />
                                Users
                            </Title>
                        </Space>
                    </Col>

                </Row>
            </Header>

            <Content style={{ margin: '24px' }}>
                <Card>
                    <Table
                        columns={columns}
                        dataSource={users}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                        }}
                    />
                </Card>


                <Modal
                    title={
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Text strong>User Details</Text>
                            </Col>
                            <Col>
                                {!isEditMode && (
                                    <Button type="primary" onClick={handleEditModal}>
                                        Edit
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    }
                    visible={viewModalVisible}
                    onCancel={() => {
                        setIsEditMode(false);
                        handleCloseModal();
                    }}
                    closable={false}
                    footer={
                        isEditMode ? (
                            <Row justify="center">
                                <Button key="cancel" onClick={() => setIsEditMode(false)} style={{ marginRight: 8 }}>
                                    Cancel
                                </Button>
                                <Button key="save" type="primary" onClick={handleSaveEdit}>
                                    Save
                                </Button>
                            </Row>
                        ) : (
                            <Row justify="end">
                                <Button key="close" onClick={handleCloseModal}>
                                    Close
                                </Button>
                            </Row>
                        )
                    }
                    width={800}
                >
                    {selectedUser && (() => {
                        // Helper to check empty value including []
                        const isEmptyValue = v =>
                            v === null ||
                            v === undefined ||
                            v === '' ||
                            (Array.isArray(v) && v.length === 0);

                        // Prepare fields
                        const editableFields = ['Name', 'username', 'email', 'is_active'];
                        const userObj = isEditMode ? editUser : selectedUser;
                        // Filter and sort fields
                        let entries = Object.entries(userObj)
                            .filter(([key]) => !excludedFields.includes(key));
                        // Separate editable and non-editable
                        const editable = entries.filter(([key]) => editableFields.includes(key));
                        const nonEditable = entries.filter(([key]) => !editableFields.includes(key));
                        // Move null/empty values to last
                        const notNullEditable = editable.filter(([_, v]) => !isEmptyValue(v));
                        const nullEditable = editable.filter(([_, v]) => isEmptyValue(v));
                        const notNullNonEditable = nonEditable.filter(([_, v]) => !isEmptyValue(v));
                        const nullNonEditable = nonEditable.filter(([_, v]) => isEmptyValue(v));
                        const finalFields = [...notNullEditable, ...notNullNonEditable, ...nullEditable, ...nullNonEditable];

                        return (
                            <Row gutter={24}>
                                {finalFields.map(([key, value]) => (
                                    <Col span={8} key={key} style={{ marginBottom: 0 }}>
                                        <div style={{ borderBottom: '1px solid #eee', padding: '12px 0' }}>
                                            <Text style={{ fontWeight: '500' }}>
                                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </Text>
                                            <br />
                                            {isEditMode && editableFields.includes(key) ? (
                                                key === 'is_active' ? (
                                                    <Checkbox
                                                        checked={!!value}
                                                        onChange={e => handleEditChange(key, e.target.checked)}
                                                        style={{ marginTop: 4 }}
                                                    >

                                                    </Checkbox>
                                                ) : (
                                                    <Input
                                                        value={isEmptyValue(value) ? '' : value}
                                                        onChange={e => handleEditChange(key, e.target.value)}
                                                        style={{ marginTop: 4 }}
                                                    />
                                                )
                                            ) : (
                                                key === 'is_active' ? (
                                                    <Checkbox checked={!!value} disabled style={{ marginTop: 4 }}>

                                                    </Checkbox>
                                                ) : (
                                                    <Text style={{ marginTop: 4 }}>
                                                        {isEmptyValue(value) ? 'N/A' : String(value)}
                                                    </Text>
                                                )
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        );
                    })()}
                </Modal>
            </Content>
        </Layout>
    )
}
export default AdminUserDetails;